/**
 * Send Invoice Button - Phase 9.17 Enhanced Email Confirmation
 * Updated Phase 10a.1: Added Edit/Preview toggle with email body preview
 * Updated Phase 10a.2: Ref-based sliding pill + PDF preview tab
 * Updated R3: TO/CC/BCC multi-pill inputs pre-filled from invoice_contacts by role
 *
 * Sends invoice via email with PDF attachment.
 * Shows full confirmation dialog with:
 * - TO / CC pill inputs (pre-filled from invoice_contacts by role)
 * - BCC text input (pre-filled from tenant settings)
 * - Reply-To (editable if not configured in settings)
 * - Subject preview (read-only)
 * - Attachment filename (read-only)
 * - Personal note (optional)
 * - Email body preview with Recipients summary (toggle)
 * - PDF attachment preview (toggle)
 */
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SendConfirmationDialog } from '@/components/invoices/SendConfirmationDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  clearSendIntent,
  formatSentAt,
  isKnownSendOutcome,
  readSendIntent,
  sendErrorDetail,
  storeSendIntent,
} from '@/lib/invoiceSendIntent';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { PillInput } from '@/components/ui/PillInput';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Send, Loader2, Paperclip, Reply, Users, Eye, Pencil, Lock, FileText, AlertCircle, History, Receipt } from 'lucide-react';
import type { AccountingContact } from '@/lib/schemas';
import type { InvoiceStatus } from '@/components/forms/InvoiceStatusSelect';
import { useEmailSettings, useEmailPreview } from '@/hooks';
import { EmailPreviewPanel } from '@/components/email/EmailPreviewPanel';
import { EmailSendHistory } from '@/components/email/EmailSendHistory';
import type { SendEvidence } from '@/lib/sendEvidence';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';

interface InvoiceContact {
  role: string;
  contact_name: string;
  contact_email: string | null;
}

// Stable empty array — avoids useEffect re-runs when invoiceContacts prop is undefined
const EMPTY_CONTACTS: InvoiceContact[] = [];

interface SendInvoiceButtonProps {
  invoiceId: string;
  invoiceNumber?: string;
  projectName?: string;
  defaultRecipient?: string;
  disabled?: boolean;
  /** Tab to open the dialog on (defaults to 'edit') */
  initialTab?: 'edit' | 'preview' | 'pdf' | 'history';
  /** Controlled open state - when provided, the component doesn't render its own trigger button */
  open?: boolean;
  /** Controlled open change handler */
  onOpenChange?: (open: boolean) => void;
  /** When true, hide the trigger button (dialog-only mode for overflow menu integration) */
  hideButton?: boolean;
  /** Phase 10b.2: Whether this invoice has expense items with attached receipts */
  hasExpenseReceipts?: boolean;
  /** R3: invoice_contacts with role for TO/CC pre-fill */
  invoiceContacts?: InvoiceContact[];
  /**
   * The invoice's current status. Drives two different things.
   *
   * `cancelled` BLOCKS the send outright: the backend's send-status guard
   * (SPEC-invoice-send-safety-2026-08-17 R2, `_SEND_BLOCKED_STATUSES`)
   * 409s it, and emailing a dead invoice would misrepresent it as current.
   * There is no unblock to offer, so the control stays disabled with the
   * reason in its tooltip.
   *
   * `draft` PROMOTES instead of blocking (v0.17 R6 HIGH-1). The backend
   * guard still refuses to email a draft — that guard is deliberate and
   * stays — but v0.17's frontend answer to it was to grey out "Send Email"
   * and put the only unblock, "Mark as Ready", in the ⋯ overflow menu, with
   * the reason visible only in a tooltip on the disabled button. A new
   * invoice is born `draft`, so the ordinary path — fill in recipients,
   * write a note, click Send — ended in a dead end. Forty lines below, this
   * same screen already carries the fix for that exact failure, for this
   * exact person: "v0.12.21 Item C: Preview PDF promoted from kebab to
   * top-line button. Christa didn't realize it was in the overflow menu."
   * And her own words on the state this produces (2026-08-10, invoice 701):
   * "I've been trying to create an invoice now for a long time and I'm
   * losing my mind... it was so hard to get to this stage even...."
   *
   * So a draft can reach the confirmation, the confirmation says plainly
   * that confirming marks it Ready and sends it, and confirming does
   * exactly that — `draft` -> `ready` through the same PUT the ⋯ menu's
   * "Mark as Ready" uses, then the send. Explicit and consented, never a
   * silent auto-promotion behind a button labelled "Send".
   *
   * `ready`/`sent`/`paid`/`partially_paid`/`overdue` are unaffected.
   */
  invoiceStatus?: InvoiceStatus;
  /** @deprecated Kept for backward compat — use invoiceContacts instead */
  accountingContacts?: AccountingContact[];
  /**
   * R1 (B2): when true, this dialog instance was entered via "Preview PDF",
   * not "Send"/"Resend". The send control is not rendered at all in this
   * mode — a button labelled Preview must not be able to send, even after
   * the user switches tabs inside the dialog (Edit/Email/PDF/History share
   * one footer). Defaults to false so every other entry point (the header
   * Send Invoice button, Resend from the overflow menu) is unaffected.
   */
  previewOnly?: boolean;
  /** R3 AC6: the invoice's send as the server's one send rule decided it (for the history tab). */
  sendEvidence?: SendEvidence | null;
}

export function SendInvoiceButton({
  invoiceId,
  invoiceNumber,
  projectName,
  defaultRecipient = '',
  disabled = false,
  initialTab,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideButton = false,
  hasExpenseReceipts = false,
  invoiceContacts = EMPTY_CONTACTS,
  accountingContacts: _accountingContacts = [], // kept for compat, not used
  previewOnly = false,
  invoiceStatus,
  sendEvidence,
}: SendInvoiceButtonProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;

  // R3: TO/CC as string arrays; BCC stays as comma-separated text input
  const [toList, setToList] = useState<string[]>([]);
  const [ccList, setCcList] = useState<string[]>([]);
  const [bccList, setBccList] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [customNote, setCustomNote] = useState('');
  const [activeView, setActiveView] = useState<'edit' | 'preview' | 'pdf' | 'history'>(initialTab ?? 'edit');
  const [fading, setFading] = useState(false);
  // Phase 10b.2. Checked by default: before v0.17 the server attached every
  // receipt whatever this box said, so a default of "off" would silently drop
  // receipts clients are used to getting. Unticking it is honoured.
  const [includeReceipts, setIncludeReceipts] = useState(true);
  const queryClient = useQueryClient();

  // R1 (B1): the "Send Email" footer button opens this confirmation instead
  // of dispatching directly. hasDispatchedRef additionally guards against a
  // double-click on the confirm action itself dispatching two sends before
  // sendMutation.isPending has re-rendered (AC5).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const hasDispatchedRef = useRef(false);

  // v0.17 R6 HIGH-1: records that THIS attempt promoted the invoice from
  // draft to ready before sending. Read afterwards so the success toast can
  // name the status change she consented to, and so a send that fails AFTER
  // a successful promotion still refreshes the page (the invoice really is
  // `ready` now; leaving the screen saying `draft` would be a fresh instance
  // of "the screen disagrees with what actually happened").
  const promotedToReadyRef = useRef(false);
  // Keep the key through a lost response and a dialog remount. Only the opaque
  // key and its age are kept in session storage (lib/invoiceSendIntent.ts).
  const sendIntentRef = useRef<string | null>(null);
  // True while the last send's outcome is unknown: the key must survive a
  // dialog close so a retry cannot email twice.
  const outcomeUnknownRef = useRef(false);
  // Set when the server says this same email already went out earlier.
  const [alreadySentAt, setAlreadySentAt] = useState<string | null>(null);
  // A receipt problem blocked the send: keep the receipts choice visible and
  // say what went wrong right under it, while the dialog stays open (A3).
  const [receiptProblem, setReceiptProblem] = useState<string | null>(null);

  // Refs for segmented control buttons
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const emailBtnRef = useRef<HTMLButtonElement>(null);
  const pdfBtnRef = useRef<HTMLButtonElement>(null);
  const historyBtnRef = useRef<HTMLButtonElement>(null);
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });

  // Measure active tab for sliding pill
  const measurePill = useCallback(() => {
    const refMap = { edit: editBtnRef, preview: emailBtnRef, pdf: pdfBtnRef, history: historyBtnRef };
    const ref = refMap[activeView];
    const btn = ref.current;
    if (btn) {
      setPillStyle({ left: btn.offsetLeft, width: btn.offsetWidth });
    }
  }, [activeView]);

  useLayoutEffect(() => {
    measurePill();
  }, [measurePill]);

  // Sync activeView with initialTab when dialog opens (render-time adjustment, no flash)
  const [prevOpen, setPrevOpen] = useState(open);
  if (open && !prevOpen) {
    setPrevOpen(true);
    if (initialTab) {
      setActiveView(initialTab);
    }
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  // PDF preview state
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<Error | null>(null);
  const [pdfRetryCount, setPdfRetryCount] = useState(0);

  // Fetch email settings
  const { data: emailSettings, isLoading: settingsLoading } = useEmailSettings();

  // Email preview (only fetches when in preview mode)
  const {
    html: previewHtml,
    isLoading: previewLoading,
    error: previewError,
    refetch: refetchPreview,
  } = useEmailPreview('invoice', invoiceId, customNote, activeView === 'preview');

  // Fetch PDF blob when switching to PDF tab
  useEffect(() => {
    if (activeView !== 'pdf') return;
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);

    // R3 AC10: looking at the PDF is read-only; it never stores, versions or archives (that is the download's job).
    api.invoices.previewPDF(invoiceId)
      .then(({ blob }) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl(url);
        setPdfLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfError(err instanceof Error ? err : new Error(String(err)));
        setPdfLoading(false);
      });

    return () => { cancelled = true; };
  }, [activeView, invoiceId, pdfRetryCount]);

  // Clean up blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // R3: Initialize TO/CC/BCC from invoice_contacts by role; fall back to defaultRecipient
  useEffect(() => {
    if (open && emailSettings) {
      setBccList(emailSettings.bcc_list?.join(', ') || '');
      setReplyTo(emailSettings.reply_to || '');
    }
    if (open) {
      const toContacts = invoiceContacts
        .filter((c) => c.role === 'to' && c.contact_email)
        .map((c) => c.contact_email!);
      const ccContacts = invoiceContacts
        .filter((c) => c.role === 'cc' && c.contact_email)
        .map((c) => c.contact_email!);

      setToList(toContacts.length ? toContacts : (defaultRecipient ? [defaultRecipient] : []));
      setCcList(ccContacts);
    }
  }, [open, emailSettings, defaultRecipient, invoiceContacts]);

  // Derived: parsed BCC array for submit and preview display
  const parsedBcc = bccList
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);

  // v0.17 R6 HIGH-1: a draft is PROMOTED on confirm, not blocked. A cancelled
  // invoice has no unblock to offer and stays blocked. See the `invoiceStatus`
  // prop docstring for the full reasoning.
  const isDraft = invoiceStatus === 'draft';
  const isSendBlockedStatus = invoiceStatus === 'cancelled';

  const sendMutation = useMutation({
    mutationFn: async () => {
      // v0.17 R6 HIGH-1: draft -> ready, then send. Sequential and awaited on
      // purpose: the backend refuses to email a draft, so a send fired before
      // the promotion lands would 409. The promotion goes through the SAME
      // endpoint the ⋯ menu's "Mark as Ready" uses (PUT /api/invoices/{id}),
      // so its own validation — no line items, totals that don't reconcile —
      // still applies and still refuses, with its real message. When it
      // refuses, this throws and NOTHING is sent.
      if (isDraft) {
        await api.invoices.update(invoiceId, { status: 'ready' });
        promotedToReadyRef.current = true;
      }
      // The retry window runs from the first request, so a retry never renews it.
      const retained = readSendIntent(invoiceId);
      const idempotencyKey = retained || crypto.randomUUID();
      sendIntentRef.current = idempotencyKey;
      if (!retained) storeSendIntent(invoiceId, idempotencyKey);
      outcomeUnknownRef.current = true;
      return api.post(`/api/invoices/${invoiceId}/send-email`, {
        idempotency_key: idempotencyKey,
        to_recipients: toList,
        cc_recipients: ccList,
        bcc_override: parsedBcc,
        reply_to_override: replyTo || null,
        custom_note: customNote || null,
        include_receipts: includeReceipts, // Phase 10b.2
      });
    },
    onSuccess: () => {
      clearSendIntent(invoiceId);
      sendIntentRef.current = null;
      outcomeUnknownRef.current = false;
      setReceiptProblem(null);
      const toLabel =
        toList.length === 1
          ? toList[0]
          : `${toList[0]} and ${toList.length - 1} other${toList.length - 1 > 1 ? 's' : ''}`;
      // v0.17 R6 HIGH-1: when we promoted, say so. She has four months of
      // corrections that evaporated and screens that disagreed with the PDF;
      // a status change she authorised has to be confirmed back to her, not
      // left to be discovered on the detail page.
      toast.success(
        promotedToReadyRef.current
          ? `Invoice marked Ready and sent to ${toLabel}`
          : `Invoice sent to ${toLabel}`
      );
      promotedToReadyRef.current = false;

      // Capture pre-send dropbox state from cache so we can detect a new sync below
      const preSendData = queryClient.getQueryData<any>(['invoices', invoiceId]);
      const hadDropboxSync = Boolean(preSendData?.invoice?.dropbox_synced_at);

      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['email-send-history', 'invoice', invoiceId] });
      setOpen(false);
      setCustomNote('');
      setActiveView(initialTab ?? 'edit');

      // R3: After BackgroundTask has had time to run (~4 s), refetch and show
      // a Dropbox toast if the sync timestamp appeared for the first time.
      // On failure (connected but still null), show an error toast.
      if (!hadDropboxSync) {
        setTimeout(async () => {
          try {
            const [, dropboxStatus] = await Promise.all([
              queryClient.refetchQueries({ queryKey: ['invoices', invoiceId] }),
              queryClient.fetchQuery({
                queryKey: ['dropbox', 'status'],
                queryFn: () => api.get('/api/integrations/dropbox/status'),
              }).catch(() => null),
            ]);
            const fresh = queryClient.getQueryData<any>(['invoices', invoiceId]);
            if (fresh?.invoice?.dropbox_synced_at) {
              toast.success('Invoice synced to Dropbox ✓');
            } else if ((dropboxStatus as any)?.connected) {
              toast.error('Dropbox sync failed — check Settings > Integrations');
            }
          } catch {
            // Non-critical — ignore refetch errors
          }
        }, 4000);
      }
    },
    onError: (error: any) => {
      const detail = sendErrorDetail(error);
      // Only a known outcome permits a fresh key. Unknown network/provider
      // outcomes retain the key so another click cannot dispatch a duplicate.
      if (isKnownSendOutcome(error)) {
        clearSendIntent(invoiceId);
        sendIntentRef.current = null;
        outcomeUnknownRef.current = false;
      }
      // Any unsettled outcome can put the "couldn't confirm" banner on the
      // invoice page; refresh it now rather than on the next page load.
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId, 'send-attempt-active'] });
      if (detail?.code === 'invoice_send_already_sent') {
        // This exact email went out earlier. Ask before sending it again.
        promotedToReadyRef.current = false;
        setAlreadySentAt(detail?.completed_at ?? '');
        return;
      }
      const message = (typeof detail === 'string' ? detail : detail?.message) ||
        error?.message || 'The send could not be confirmed. Please check its status before trying again.';
      if (detail?.code === 'invoice_receipt_unavailable' || detail?.code === 'invoice_receipts_too_large') {
        setReceiptProblem(message);
      }
      // v0.17 R6 HIGH-1: if the promotion landed and only the SEND failed,
      // the invoice really is `ready` now. Refresh so the screen says so —
      // otherwise the next thing she sees is a page still calling it a draft
      // for an invoice we already changed, which is §3.2 all over again. Her
      // own words on that class: "I changed it last time... and it's gone
      // back". Say what happened, in order.
      if (promotedToReadyRef.current) {
        // Prefix match — this also covers the detail page's
        // ['invoices', id, 'full'] key, same as onSuccess above.
        queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] });
        queryClient.invalidateQueries({ queryKey: ['invoices'] });
        toast.error(`Invoice marked Ready. ${message}`, { duration: Infinity });
      } else {
        // A3: a send refusal stays until she closes it (same as the detail
        // page's status errors). A 4-second toast can vanish before it's read.
        toast.error(message, { duration: Infinity });
      }
      promotedToReadyRef.current = false;
    },
  });

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // A closed dialog with a known outcome has no retry left to protect.
      if (!sendMutation.isPending && !outcomeUnknownRef.current) {
        clearSendIntent(invoiceId);
        sendIntentRef.current = null;
      }
      // Reset form when closing
      setCustomNote('');
      setIncludeReceipts(true); // Phase 10b.2
      setReceiptProblem(null);
      setActiveView(initialTab ?? 'edit');
      setFading(false);
      setConfirmOpen(false);
      hasDispatchedRef.current = false;
      promotedToReadyRef.current = false;
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      setPdfError(null);
    }
  };

  const handleConfirmOpenChange = (newConfirmOpen: boolean) => {
    setConfirmOpen(newConfirmOpen);
    if (newConfirmOpen) {
      hasDispatchedRef.current = false;
    }
  };

  const handleConfirmSend = () => {
    if (hasDispatchedRef.current || sendMutation.isPending) return;
    hasDispatchedRef.current = true;
    sendMutation.mutate();
    setConfirmOpen(false);
  };

  // "Send again" after being told this email already went out: the old key is
  // gone, so this is a new send, started only because she asked for it.
  const handleSendAgain = () => {
    setAlreadySentAt(null);
    if (sendMutation.isPending) return;
    clearSendIntent(invoiceId);
    sendIntentRef.current = null;
    sendMutation.mutate();
  };

  // Smooth cross-fade between views
  const switchView = (newView: 'edit' | 'preview' | 'pdf' | 'history') => {
    if (newView === activeView || fading) return;
    setFading(true);
    setTimeout(() => {
      setActiveView(newView);
      setFading(false);
    }, 200);
  };

  const displayNumber = invoiceNumber || `INV-${invoiceId.slice(0, 8)}`;
  const subject = `Invoice #${displayNumber}${projectName ? ` - ${projectName}` : ''}`;
  const attachmentName = `Invoice-${displayNumber}.pdf`;

  // v0.17 R6 HIGH-1: only `cancelled` blocks the control now. A draft is
  // sendable from here — confirming promotes it to `ready` first, and the
  // confirmation says so. See the `invoiceStatus` prop docstring.
  // Validation: TO must have at least one address
  const canSend = toList.length > 0 && !!replyTo && !isSendBlockedStatus;
  const replyToFromSettings = Boolean(emailSettings?.reply_to);

  return (
    <>
      {!hideButton && (
        <Button
          onClick={() => handleOpenChange(true)}
          disabled={disabled}
          variant="default"
          size="sm"
          data-testid="send-invoice-button"
        >
          <Mail className="w-4 h-4 mr-2" />
          Send Invoice
        </Button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={`max-h-[90vh] !flex !flex-col transition-[max-width] duration-300 ${
            activeView === 'pdf'
              ? 'sm:max-w-[95vw] xl:max-w-[1200px]'
              : activeView === 'preview'
                ? 'sm:max-w-[700px]'
                : activeView === 'history'
                  ? 'sm:max-w-[600px]'
                  : 'sm:max-w-[540px]'
          }`}
        >
          <DialogHeader>
            <DialogTitle className="truncate">Send Invoice {displayNumber}{projectName ? ` — ${projectName}` : ''}</DialogTitle>
            <DialogDescription>
              Review and confirm email details before sending.
            </DialogDescription>
          </DialogHeader>

          {/* Edit / Email / PDF Segmented Control with sliding pill */}
          <div className="flex items-center">
            <div className="bg-muted rounded-lg p-1 relative flex gap-0.5">
              {/* Sliding pill background */}
              {pillStyle.width > 0 && (
                <div
                  className="absolute top-1 bottom-1 rounded-md bg-background shadow-sm transition-all duration-200 ease-in-out"
                  style={{
                    width: pillStyle.width,
                    transform: `translateX(${pillStyle.left}px)`,
                    left: 0,
                  }}
                />
              )}
              <button
                ref={editBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'edit'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('edit')}
                data-testid="edit-toggle"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Edit
              </button>
              <button
                ref={emailBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'preview'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('preview')}
                data-testid="preview-toggle"
              >
                <Eye className="w-3.5 h-3.5 mr-1.5" />
                Email
              </button>
              <button
                ref={pdfBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'pdf'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('pdf')}
                data-testid="pdf-toggle"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                PDF
              </button>
              <button
                ref={historyBtnRef}
                className={`relative z-10 inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  activeView === 'history'
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => switchView('history')}
                data-testid="history-toggle"
              >
                <History className="w-3.5 h-3.5 mr-1.5" />
                History
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className={`overflow-y-auto scrollbar-thin flex-1 min-h-0 -mx-6 px-6 transition-opacity duration-200 ease-in-out ${fading ? 'opacity-0' : 'opacity-100'}`}>
            {settingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                {/* Subject + Attachment metadata card */}
                <div className="bg-muted/50 rounded-lg px-3 py-2.5 mt-3">
                  <div className="flex items-start gap-3 text-sm">
                    <span className="text-muted-foreground w-20 shrink-0">Subject:</span>
                    <span className="font-medium">{subject}</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm mt-2">
                    <span className="text-muted-foreground w-20 shrink-0">Attachment:</span>
                    <span className="flex items-center gap-1.5">
                      <Paperclip className="w-3.5 h-3.5" />
                      {attachmentName}
                    </span>
                  </div>
                </div>

                {activeView === 'edit' ? (
                  <div className="space-y-4 py-2">
                    {/* v0.14 (Dmitri sweep): prominent banner when the Send dialog
                        opens with no TO recipients. Submit is already disabled
                        via canSend; the banner makes the reason loud + explicit
                        instead of letting the user hunt the small inline hint.
                        Pattern follows commit 285cf0b8. */}
                    {toList.length === 0 && (
                      <div
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2"
                        data-testid="send-empty-to-banner"
                        role="alert"
                      >
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="text-sm text-amber-900">
                          <p className="font-medium">No recipients selected.</p>
                          <p className="text-xs mt-0.5">
                            Add at least one TO recipient before sending.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* TO */}
                    <div>
                      <Label htmlFor="to-pill" className="text-sm font-medium">
                        To <span className="text-red-500">*</span>
                      </Label>
                      <PillInput
                        id="to-pill"
                        value={toList}
                        onChange={setToList}
                        placeholder="client@example.com"
                        className="mt-1.5"
                        data-testid="to-pill-input"
                      />
                      {toList.length === 0 && (
                        <p className="text-xs text-destructive mt-1" data-testid="to-empty-error">
                          At least one TO recipient required.
                        </p>
                      )}
                      {toList.length > 0 && invoiceContacts.some((c) => c.role === 'to') && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Pre-filled from invoice contacts. Edit for this send only.
                        </p>
                      )}
                    </div>

                    {/* CC */}
                    <div>
                      <Label htmlFor="cc-pill" className="text-sm font-medium">
                        CC
                      </Label>
                      <PillInput
                        id="cc-pill"
                        value={ccList}
                        onChange={setCcList}
                        placeholder="cc@example.com"
                        className="mt-1.5"
                        data-testid="cc-pill-input"
                      />
                      {ccList.length > 0 && invoiceContacts.some((c) => c.role === 'cc') && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Pre-filled from invoice contacts. Edit for this send only.
                        </p>
                      )}
                    </div>

                    {/* BCC */}
                    <div>
                      <Label htmlFor="bcc" className="text-sm font-medium flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5" />
                        BCC (team copies)
                      </Label>
                      <Input
                        id="bcc"
                        type="text"
                        value={bccList}
                        onChange={(e) => setBccList(e.target.value)}
                        placeholder="accounting@company.com, admin@company.com"
                        className="mt-1.5"
                        data-testid="bcc-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Comma-separated. {emailSettings?.bcc_list?.length ? 'Defaults from Settings.' : 'No defaults configured.'} Edit for this send only.
                      </p>
                    </div>

                    {/* Reply-To */}
                    <div>
                      <Label htmlFor="reply-to" className="text-sm font-medium flex items-center gap-1.5">
                        <Reply className="w-3.5 h-3.5" />
                        Reply-To <span className="text-red-500">*</span>
                      </Label>
                      {replyToFromSettings ? (
                        <div className="mt-1.5 px-3 py-2 bg-muted rounded-md text-sm flex items-center justify-between">
                          <span>{replyTo}</span>
                          <Lock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        </div>
                      ) : (
                        <Input
                          id="reply-to"
                          type="email"
                          value={replyTo}
                          onChange={(e) => setReplyTo(e.target.value)}
                          placeholder="proposals@yourcompany.com"
                          className="mt-1.5"
                          data-testid="reply-to-input"
                        />
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {replyToFromSettings
                          ? 'Configured in Settings > Email. Client replies go here.'
                          : 'Required. Where client replies will go.'}
                      </p>
                    </div>

                    {/* Custom Note */}
                    <div className="border-t pt-4">
                      <Label htmlFor="note" className="text-sm font-medium">
                        Personal Note (optional)
                      </Label>
                      <Textarea
                        id="note"
                        value={customNote}
                        onChange={(e) => setCustomNote(e.target.value)}
                        placeholder="Add a personal message that will appear in the email body..."
                        rows={4}
                        className="mt-1.5"
                        data-testid="custom-note-input"
                      />
                    </div>

                    {/* Phase 10b.2: Include Receipts checkbox */}
                    {(hasExpenseReceipts || receiptProblem) && (
                      <div className="flex items-start gap-2 border-t pt-4" data-testid="include-receipts-section">
                        <input
                          type="checkbox"
                          id="include-receipts"
                          checked={includeReceipts}
                          onChange={(e) => setIncludeReceipts(e.target.checked)}
                          data-testid="include-receipts-checkbox"
                          className="mt-0.5 h-4 w-4 rounded border-gray-300"
                        />
                        <div>
                          <Label htmlFor="include-receipts" className="text-sm font-medium cursor-pointer flex items-center gap-1.5">
                            <Receipt className="w-3.5 h-3.5" />
                            Include expense receipts
                          </Label>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            The receipt files for this invoice's expenses are attached to the email.
                          </p>
                          {receiptProblem && (
                            <p
                              className="mt-1.5 flex items-start gap-1.5 text-xs text-destructive"
                              role="alert"
                              data-testid="include-receipts-problem"
                            >
                              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                              <span>{receiptProblem}</span>
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : activeView === 'preview' ? (
                  /* Email Preview View */
                  <div className="py-2">
                    {/* R3: Recipients summary card at top of preview */}
                    <div
                      className="bg-muted/50 rounded-lg px-3 py-2.5 mb-4 text-sm"
                      data-testid="recipients-summary"
                    >
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Recipients
                      </p>
                      <div className="space-y-1">
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground font-medium w-8 shrink-0">TO</span>
                          <span className="break-all">
                            {toList.length > 0 ? toList.join(', ') : <em>none</em>}
                          </span>
                        </div>
                        {ccList.length > 0 && (
                          <div className="flex items-start gap-2">
                            <span className="text-muted-foreground font-medium w-8 shrink-0">CC</span>
                            <span className="break-all">{ccList.join(', ')}</span>
                          </div>
                        )}
                        <div className="flex items-start gap-2">
                          <span className="text-muted-foreground font-medium w-8 shrink-0">BCC</span>
                          <span>
                            {parsedBcc.length > 0
                              ? `${parsedBcc.length} address${parsedBcc.length > 1 ? 'es' : ''}`
                              : <em>none</em>}
                          </span>
                        </div>
                      </div>
                    </div>
                    <EmailPreviewPanel
                      html={previewHtml}
                      isLoading={previewLoading}
                      error={previewError}
                      onRetry={() => refetchPreview()}
                    />
                  </div>
                ) : activeView === 'history' ? (
                  /* Send History View (Phase 10a.3) */
                  <div className="py-2">
                    <EmailSendHistory entityType="invoice" entityId={invoiceId} sendEvidence={sendEvidence} />
                  </div>
                ) : (
                  /* PDF Preview View */
                  <div className="py-2">
                    <div className="rounded-lg border bg-muted/30 p-4 animate-in fade-in-0 duration-200" data-testid="pdf-preview-panel">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
                        <FileText className="w-4 h-4" />
                        PDF Attachment Preview
                      </div>
                      {pdfLoading ? (
                        <div className="flex flex-col items-center justify-center py-12 gap-3">
                          <Loader2
                            className="w-6 h-6 animate-spin text-muted-foreground"
                            data-testid="pdf-preview-loading"
                          />
                          <p className="text-sm text-muted-foreground">Loading PDF...</p>
                        </div>
                      ) : pdfError ? (
                        <div className="flex flex-col items-center justify-center py-8 gap-3">
                          <AlertCircle className="w-6 h-6 text-destructive" />
                          <p className="text-sm text-destructive">Failed to load PDF preview</p>
                          <p className="text-xs text-muted-foreground">{pdfError.message}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPdfRetryCount((c) => c + 1)}
                            data-testid="pdf-preview-retry"
                          >
                            Retry
                          </Button>
                        </div>
                      ) : pdfBlobUrl ? (
                        <>
                          {/* Desktop: inline PDF viewer */}
                          <div className="hidden sm:block rounded border bg-white overflow-hidden">
                            <embed
                              src={`${pdfBlobUrl}#navpanes=0&view=FitH`}
                              type="application/pdf"
                              className="w-full border-0"
                              style={{ height: '70vh' }}
                              data-testid="pdf-preview-iframe"
                            />
                          </div>
                          {/* Mobile: download/open link (PDF iframes don't work well on mobile) */}
                          <div className="sm:hidden flex flex-col items-center gap-3 py-8">
                            <FileText className="w-10 h-10 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground text-center">
                              PDF preview is best viewed on desktop.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(pdfBlobUrl, '_blank')}
                              data-testid="pdf-open-mobile"
                            >
                              Open PDF
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sticky footer with actions */}
          <div className="flex items-center justify-end gap-2 border-t pt-3 mt-auto pb-[env(safe-area-inset-bottom)]">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="max-sm:flex-1">
              Cancel
            </Button>
            {/* R1 (B2): the send control is entirely absent when this dialog
                instance was entered via "Preview PDF" — a button labelled
                Preview must not be able to send, from any of its tabs. */}
            {!previewOnly && (
              <div className="max-sm:flex-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                      <Button
                        size="sm"
                        onClick={() => handleConfirmOpenChange(true)}
                        disabled={!canSend || sendMutation.isPending || settingsLoading}
                        className="disabled:opacity-40 w-full"
                        data-testid="send-email-button"
                      >
                        {sendMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-1.5" />
                        )}
                        {sendMutation.isPending ? 'Sending...' : 'Send Email'}
                      </Button>
                      </span>
                    </TooltipTrigger>
                    {!canSend && (
                      <TooltipContent data-testid="send-email-disabled-tooltip">
                        {isSendBlockedStatus
                          ? 'This invoice is cancelled and cannot be emailed.'
                          : toList.length === 0 && !replyTo
                            ? 'Enter at least one TO recipient and reply-to address'
                            : toList.length === 0
                              ? 'At least one TO recipient required'
                              : 'Enter a reply-to email address'}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* R1 (B1): confirmation naming the invoice + every recipient address
          the email will go to. A single click on "Send Email" above no
          longer dispatches the send — it opens this dialog instead. */}
      <SendConfirmationDialog
        open={confirmOpen}
        onOpenChange={handleConfirmOpenChange}
        invoiceNumber={displayNumber}
        to={toList}
        cc={ccList}
        bcc={parsedBcc}
        pending={sendMutation.isPending}
        willMarkReady={isDraft}
        onConfirm={handleConfirmSend}
      />

      <AlertDialog open={alreadySentAt !== null} onOpenChange={(next) => { if (!next) setAlreadySentAt(null); }}>
        <AlertDialogContent data-testid="already-sent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>This invoice was already emailed</AlertDialogTitle>
            <AlertDialogDescription>
              Invoice #{displayNumber} was sent on {formatSentAt(alreadySentAt)}. Nothing new was sent just now.
              Do you want to email it again?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="already-sent-cancel">{"Don't send"}</AlertDialogCancel>
            <AlertDialogAction data-testid="already-sent-send-again" onClick={handleSendAgain}>
              Send again
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
